import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { deviceTimezone, lotZoneLabel, resolveLotTimeLanguage } from '../lib/lot-time';
import { clockInTimeZone, minutesUntilLocalClock } from '../lib/time-selection';
import { IconMinus, IconPlus } from './icons';

export function DurationEditor({
  value,
  onChange,
  onModeChange,
  maxMinutes = 60 * 24 * 30,
  timezone,
}: {
  value: number;
  onChange: (minutes: number) => void;
  /** Fired when duration/departure semantics flip: the active quote must die. */
  onModeChange?: () => void;
  maxMinutes?: number;
  timezone?: string;
}) {
  const { t, i18n } = useTranslation();
  // Until the lot payload arrives the device zone stands in — labelled as
  // itself via Intl, never mislabelled as the lot's zone.
  const zone = timezone ?? deviceTimezone();
  const [mode, setMode] = useState<'duration' | 'departure'>('duration');
  const [hours, setHours] = useState(Math.floor(value / 60));
  const [minutes, setMinutes] = useState(value % 60);
  const [departure, setDeparture] = useState(() => {
    const target = clockInTimeZone(new Date(Date.now() + value * 60_000), zone);
    return `${String(target.hour).padStart(2, '0')}:${String(target.minute).padStart(2, '0')}`;
  });
  const zoneLabel = lotZoneLabel(zone, resolveLotTimeLanguage(i18n.language));

  const setDuration = (nextHours: number, nextMinutes: number) => {
    let safeHours = Math.max(0, Math.min(Math.floor(maxMinutes / 60), nextHours || 0));
    let safeMinutes = Math.max(0, Math.min(59, nextMinutes || 0));
    if (safeHours * 60 + safeMinutes > maxMinutes) {
      safeHours = Math.floor(maxMinutes / 60);
      safeMinutes = maxMinutes % 60;
    }
    setHours(safeHours);
    setMinutes(safeMinutes);
    onChange(safeHours * 60 + safeMinutes);
  };

  const setDepartureTime = (time: string) => {
    setDeparture(time);
    const [hour, minute] = time.split(':').map(Number);
    if (hour === undefined || minute === undefined) return onChange(0);
    onChange(Math.min(maxMinutes, minutesUntilLocalClock(hour, minute, zone)));
  };

  const switchMode = (nextMode: 'duration' | 'departure') => {
    if (nextMode === mode) return;
    setMode(nextMode);
    // A quote priced under the old input semantics must not survive the flip,
    // even when the minute count happens to coincide (review P0-2).
    onModeChange?.();
    if (nextMode === 'departure') {
      const target = clockInTimeZone(new Date(Date.now() + Math.max(1, value) * 60_000), zone);
      setDeparture(`${String(target.hour).padStart(2, '0')}:${String(target.minute).padStart(2, '0')}`);
    } else {
      setHours(Math.floor(value / 60));
      setMinutes(value % 60);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 rounded-lg bg-surface-muted p-1">
        {(['duration', 'departure'] as const).map((item) => (
          <button
            key={item}
            type="button"
            aria-pressed={mode === item}
            onClick={() => switchMode(item)}
            className={`min-h-11 rounded-md text-sm font-medium ${mode === item ? 'bg-white text-ink' : 'text-muted'}`}
          >
            {t(`duration.mode.${item}`)}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-4 gap-2">
        {[60, 120, 240, 1440].map((choice) => (
          <button
            key={choice}
            type="button"
            onClick={() => {
              // Quick choices are duration semantics; leaving departure mode
              // through them is still a mode flip and kills the quote.
              if (mode !== 'duration') onModeChange?.();
              setMode('duration');
              setHours(Math.floor(choice / 60));
              setMinutes(choice % 60);
              onChange(choice);
            }}
            className={`min-h-12 rounded-lg border text-[13px] font-semibold ${value === choice ? 'border-ink bg-ink text-white' : 'border-hairline-strong'}`}
          >
            {choice === 1440 ? t('duration.quick.allDay') : t('duration.quick.hours', { count: choice / 60 })}
          </button>
        ))}
      </div>

      {mode === 'duration' ? (
        <div className="flex items-start justify-center gap-4">
          <Stepper
            label={t('duration.hours')}
            value={hours}
            max={Math.floor(maxMinutes / 60)}
            onChange={(next) => setDuration(next, minutes)}
          />
          <Stepper
            label={t('duration.minutes')}
            value={minutes}
            max={59}
            step={5}
            onChange={(next) => setDuration(hours, next)}
          />
        </div>
      ) : (
        <label className="mx-auto flex w-56 flex-col gap-1 text-center text-xs text-muted">
          {t('duration.departure')}
          <input
            type="time"
            value={departure}
            onChange={(event) => setDepartureTime(event.target.value)}
            className="num h-[70px] rounded-lg border-[1.5px] border-ink bg-white px-3 text-center text-3xl font-bold text-ink"
          />
          <span>{t('duration.departureZone', { zone: zoneLabel })}</span>
        </label>
      )}
    </div>
  );
}

/**
 * Compact − / value / + stepper that also takes direct typing. The field is
 * text + numeric, never type="number": the native spinner arrows must not
 * exist on any engine, and every touch target is at least 44px.
 */
function Stepper({
  label,
  value,
  max,
  step = 1,
  onChange,
}: {
  label: string;
  value: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
}) {
  const { t } = useTranslation();
  // Draft mirrors the committed value except while it agrees numerically — an
  // empty field mid-typing must not snap back to "0" under the driver's thumb.
  const [draft, setDraft] = useState(String(value));
  useEffect(() => {
    setDraft((current) => (Number(current || '0') === value ? current : String(value)));
  }, [value]);

  const clamp = (next: number) => Math.max(0, Math.min(max, next));
  // Stepping from an off-step value snaps to the adjacent multiple, so − after
  // typing 57 minutes gives 55, not 52.
  const stepBy = (direction: 1 | -1) => {
    const snapped =
      direction > 0 ? Math.floor(value / step) * step + step : Math.ceil(value / step) * step - step;
    onChange(clamp(snapped));
  };
  const type = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 4);
    setDraft(digits);
    onChange(Number(digits || '0'));
  };

  return (
    <div className="flex flex-col items-center gap-1 text-xs text-muted">
      <div className="flex items-stretch overflow-hidden rounded-lg border-[1.5px] border-ink bg-white">
        <button
          type="button"
          aria-label={t('duration.decrease', { unit: label })}
          disabled={value <= 0}
          onClick={() => stepBy(-1)}
          className="flex h-11 w-11 items-center justify-center text-ink disabled:opacity-30"
        >
          <IconMinus className="h-5 w-5" />
        </button>
        <input
          aria-label={label}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={draft}
          onChange={(event) => type(event.target.value)}
          onBlur={() => setDraft(String(value))}
          onKeyDown={(event) => {
            if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
            event.preventDefault();
            stepBy(event.key === 'ArrowUp' ? 1 : -1);
          }}
          className="num h-11 w-14 appearance-none border-x border-hairline bg-white text-center text-xl font-bold text-ink [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
        <button
          type="button"
          aria-label={t('duration.increase', { unit: label })}
          disabled={value >= max}
          onClick={() => stepBy(1)}
          className="flex h-11 w-11 items-center justify-center text-ink disabled:opacity-30"
        >
          <IconPlus className="h-5 w-5" />
        </button>
      </div>
      <span>{label}</span>
    </div>
  );
}
